import { FormSelect } from "../../../components/ui/select";
import { EnterSearchInput } from "../../../components/ui/EnterSearchInput";
import { TablePagination } from "../../../components/ui/TablePagination";
import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Cpu, Plus, Search } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import {
  AlertBanner,
  ContentCard,
  DataTable,
  EmptyState,
  LoadingState,
  PageHeader,
  ui
} from "../../../components/ui";
import { customerService } from "../../../services/customerService";
import {
  assetRecordId,
  customerLabel,
  customerRecordId,
  fetchCustomerChoices,
  mapAssetFromApi,
  withSelectedCustomer
} from "./assetFormModel";

const PAGE_SIZE = 10;
const ASSETS_ADD_PATH = "/services/CRM/Customer/Customer-Assets/add";
const assetsEditPath = (assetId) =>
  `/services/CRM/Customer/Customer-Assets/edit/${encodeURIComponent(assetId)}`;

const filterControlClass =
  "h-11 w-full rounded-xl border border-(--color-text-border) bg-white px-3 text-sm text-foreground shadow-none focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 disabled:cursor-not-allowed disabled:opacity-50";

function statusMeta(statusRaw) {
  const status = String(statusRaw || "Active");
  if (status.toLowerCase() === "inactive") {
    return { label: i18n.t("status.inactive"), className: "border-rose-200 text-rose-700 bg-rose-50" };
  }
  return { label: i18n.t("status.active"), className: "border-emerald-200 text-emerald-700 bg-emerald-50" };
}

export function CustomerAssetsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customerId, setCustomerId] = useState(() => searchParams.get("customerId") || "");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [assetSearch, setAssetSearch] = useState("");
  const [assets, setAssets] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!customerId || selectedCustomer) return;
    let active = true;
    customerService.getCustomerById(customerId).then((customer) => {
      if (active && customer) setSelectedCustomer(customer);
    }).catch(() => {});
    return () => {
      active = false;
    };
  }, [customerId, selectedCustomer]);

  const loadCustomers = useCallback(async () => {
    if (customersLoaded || customersLoading) return;
    setCustomersLoading(true);
    try {
      const list = await fetchCustomerChoices("");
      setCustomers(list);
      setCustomersLoaded(true);
    } catch (err) {
      console.error("Failed to load customers:", err);
      setErrorMsg(err?.message || i18n.t("crm.failedToLoadCustomerAssets"));
    } finally {
      setCustomersLoading(false);
    }
  }, [customersLoaded, customersLoading]);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const apiPage = page - 1;
      let response;
      if (customerId && assetSearch) {
        response = await customerService.searchAssetsByName(
          customerId,
          assetSearch,
          apiPage,
          PAGE_SIZE,
          "assetName",
          "ASC"
        );
      } else if (customerId) {
        response = await customerService.getAssetsByCustomer(
          customerId,
          apiPage,
          PAGE_SIZE,
          "assetId",
          "ASC"
        );
      } else {
        response = await customerService.searchAssets({
          page: apiPage,
          size: PAGE_SIZE,
          sortBy: "assetId",
          sortDir: "ASC",
          assetName: assetSearch || undefined
        });
      }
      const list = Array.isArray(response?.assets) ? response.assets : [];
      setAssets(list.map((asset) => mapAssetFromApi(asset)));
      setTotalPages(response?.totalPages || 1);
      setTotalElements(response?.totalElements ?? list.length);
    } catch (err) {
      console.error("Failed to load customer assets:", err);
      setAssets([]);
      setTotalPages(1);
      setTotalElements(0);
      setErrorMsg(err?.message || i18n.t("crm.failedToLoadCustomerAssets"));
    } finally {
      setLoading(false);
    }
  }, [customerId, assetSearch, page]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const selectCustomer = (nextId) => {
    setCustomerId(nextId);
    setPage(1);
    const match = customers.find((customer) => String(customerRecordId(customer)) === String(nextId));
    setSelectedCustomer(match || null);
    const next = new URLSearchParams(searchParams);
    if (nextId) next.set("customerId", nextId);
    else next.delete("customerId");
    setSearchParams(next, { replace: true });
  };

  const clearAllFilters = () => {
    setCustomerId("");
    setSelectedCustomer(null);
    setAssetSearch("");
    setPage(1);
    const next = new URLSearchParams(searchParams);
    next.delete("customerId");
    setSearchParams(next, { replace: true });
  };

  const hasActiveFilters = Boolean(customerId || assetSearch);

  const goToCreate = () => {
    navigate(
      customerId
        ? `${ASSETS_ADD_PATH}?customerId=${encodeURIComponent(customerId)}`
        : ASSETS_ADD_PATH
    );
  };

  const goToEdit = (asset) => {
    const id = assetRecordId(asset);
    if (!id) return;
    const custId = asset.customerId || customerId;
    const editPath = assetsEditPath(id);
    navigate(
      custId
        ? `${editPath}?customerId=${encodeURIComponent(custId)}`
        : editPath
    );
  };

  const customerOptions = withSelectedCustomer(customers, selectedCustomer);
  const tableColumns = [
    { label: i18n.t("crm.assetName").replace(/\s*\*$/, ""), key: "name" },
    { label: i18n.t("common.category2").replace(/\s*\*$/, ""), key: "category" },
    { label: i18n.t("crm.serialNumber").replace(/\s*\*$/, ""), key: "serial" },
    { label: i18n.t("crm.amcType"), key: "amc" },
    { label: i18n.t("crm.assetStatus"), key: "status" }
  ];

  const emptyTitle = assetSearch || customerId
    ? i18n.t("crm.noAssetsMatchSearch")
    : i18n.t("crm.noAssetsForThisCustomer");

  const emptyMessage = assetSearch || customerId
    ? i18n.t("crm.tryClearingFiltersOrDifferentAssetName")
    : i18n.t("crm.noAssetsRegisteredForCustomer");

  return (
    <AppLayout>
      <PageHeader
        back={<PageBackLink to="/" />}
        title={i18n.t("crm.customerAssets")}
        action={
          <button type="button" onClick={goToCreate} className={ui.primaryBtn}>
            <Plus className="h-4 w-4" />
            <Trans i18nKey="formActions.addAsset" />
          </button>
        }
      />

      {errorMsg && <AlertBanner type="error">{errorMsg}</AlertBanner>}

      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
          <div className="relative w-full sm:w-[280px] lg:shrink-0">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 per-file-hint"
            />
            <EnterSearchInput
              value={assetSearch}
              onChange={(e) => {
                setAssetSearch(String(e.target.value || "").trim());
                setPage(1);
              }}
              placeholder={i18n.t("crm.searchByAssetName")}
              aria-label={i18n.t("crm.searchByAssetName")}
              className={`${filterControlClass} pl-9`}
            />
          </div>

          <div className="w-full sm:w-[280px] lg:w-auto lg:min-w-[280px]">
            <FormSelect
              value={customerId}
              onChange={(e) => selectCustomer(e.target.value)}
              onOpenChange={(open) => {
                if (open) loadCustomers();
              }}
              aria-label={i18n.t("crm.selectCustomer")}
              className={filterControlClass}
            >
              <option value="">
                {customersLoading ? i18n.t("common.loading") : i18n.t("crm.selectCustomer")}
              </option>
              {customerOptions.map((customer) => {
                const id = customerRecordId(customer);
                return (
                  <option key={id} value={String(id)}>
                    {customerLabel(customer)}
                  </option>
                );
              })}
            </FormSelect>
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              className="text-sm font-semibold per-file-hint hover:text-primary transition whitespace-nowrap self-start lg:self-center"
              onClick={clearAllFilters}
            >
              <Trans i18nKey="formActions.clearFilters" />
            </button>
          )}
        </div>
      </div>

      <ContentCard>
        {loading ? (
          <LoadingState message={i18n.t("crm.loadingCustomerAssets")} />
        ) : assets.length === 0 ? (
          <EmptyState
            icon={Cpu}
            title={emptyTitle}
            message={emptyMessage}
            action={
              <Button type="button" onClick={goToCreate} className={ui.primaryBtn}>
                <Plus className="h-4 w-4" />
                <Trans i18nKey="formActions.addAsset" />
              </Button>
            }
          />
        ) : (
          <>
            <DataTable columns={tableColumns} minWidth="980px">
              {assets.map((asset) => {
                const id = assetRecordId(asset);
                const meta = statusMeta(asset.assetStatus);
                return (
                  <tr
                    key={id || asset.assetName}
                    onClick={() => goToEdit(asset)}
                    title={i18n.t("crm.editAsset")}
                    className="cursor-pointer hover:bg-workspace/30 transition-colors"
                  >
                    <td className="px-4 py-3.5 font-semibold text-foreground">{asset.assetName || "—"}</td>
                    <td className="px-4 py-3.5 text-foreground">{asset.assetCategory || "—"}</td>
                    <td className="px-4 py-3.5 text-foreground">{asset.serialNumber || "—"}</td>
                    <td className="px-4 py-3.5 text-foreground">{asset.amcType || "—"}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${meta.className}`}>
                        {meta.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </DataTable>

            <div className="md:hidden divide-y divide-(--color-text-border)/35">
              {assets.map((asset) => {
                const id = assetRecordId(asset);
                const meta = statusMeta(asset.assetStatus);
                return (
                  <div
                    key={`mobile-${id || asset.assetName}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => goToEdit(asset)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goToEdit(asset);
                      }
                    }}
                    title={i18n.t("crm.editAsset")}
                    className="p-4 space-y-3 cursor-pointer hover:bg-workspace/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-foreground">{asset.assetName || "—"}</p>
                        <p className="text-xs per-file-hint mt-1">{asset.assetCategory || "—"}</p>
                      </div>
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${meta.className}`}>
                        {meta.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <TablePagination
              page={page}
              totalPages={totalPages}
              totalItems={totalElements}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              showSummary={false}
            />
          </>
        )}
      </ContentCard>
    </AppLayout>
  );
}
